import trackerAiApi from './trackerAiApi';
import {
  buildSourceInsightsBody,
  buildTrackerRangeBody,
  type TrackerDateRangeQuery,
  type TrackerListBody
} from './queryParams';
import type {
  AiModel,
  BrandReextractReport,
  CompetitorInsight,
  CompetitorIntelligence,
  Domain,
  ExecutionInsight,
  Project,
  ProjectActivityRange,
  ProjectBrandAlias,
  ProjectPromptListResult,
  ProjectTopicListResult,
  ProjectVisibilitySummary,
  PromptBrandAggregation,
  PromptOverview,
  PromptExecutionHistoryResponse,
  PromptHistoryItem,
  PromptProviderResponse,
  EnqueuePromptRunsResult,
  PromptRunBatchStatus,
  RunPromptResult,
  Topic,
  UserProjectInsight,
  UserPromptInsight,
  VisibilityTrendResponse,
  ShareOfVoiceTrendResponse
} from '../types';
import type {
  BrandSourceInsightsResult,
  CompetitorSourceGapResult,
  PromptSourceInsightsResult,
  SourceFilterOptionsResult,
  SourceInsightsDomainsResult,
  SourceInsightsSummary,
  SourceInsightsTrendResult,
  SourceInsightsTypesResult
} from '../types';

const trackerEndpointsApi = trackerAiApi.injectEndpoints({
  endpoints: build => ({
    getProjects: build.query<Project[], void>({
      query: () => '/projects',
      providesTags: ['Projects']
    }),

    ensureProject: build.mutation<
      { id: string },
      { slug: string; domain: string; aliases?: string[] }
    >({
      query: body => ({
        url: '/projects/ensure',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Projects']
    }),

    getProject: build.query<Project, string>({
      query: id => `/projects/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Project', id }]
    }),

    deleteProject: build.mutation<void, string>({
      query: id => ({
        url: `/projects/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Projects']
    }),

    getProjectActivityRange: build.query<ProjectActivityRange, string>({
      query: id => `/projects/${id}/activity-range`,
      providesTags: (_r, _e, id) => [{ type: 'ProjectActivityRange', id }]
    }),

    getProjectInsight: build.query<UserProjectInsight, string>({
      query: id => `/projects/${id}/insight`,
      providesTags: (_r, _e, id) => [{ type: 'ProjectInsight', id }]
    }),

    getProjectPrompts: build.query<
      ProjectPromptListResult,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/prompts/list`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: ['Prompts']
    }),

    /** Paginated prompts table; `filters.topicId` scopes to one topic. */
    getProjectPromptList: build.query<
      ProjectPromptListResult,
      { projectId: string } & TrackerListBody
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/prompts/list`,
        method: 'POST',
        body
      }),
      providesTags: ['Prompts']
    }),

    /** Grid page fetches (AgGridWrapper server mode); never touches the page-1 query cache. */
    fetchProjectPromptListPage: build.mutation<
      ProjectPromptListResult,
      { projectId: string } & TrackerListBody
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/prompts/list`,
        method: 'POST',
        body
      })
    }),

    getProjectTopicList: build.query<
      ProjectTopicListResult,
      { projectId: string } & TrackerListBody
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/topics/list`,
        method: 'POST',
        body
      }),
      providesTags: ['Topics']
    }),

    fetchProjectTopicListPage: build.mutation<
      ProjectTopicListResult,
      { projectId: string } & TrackerListBody
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/topics/list`,
        method: 'POST',
        body
      })
    }),

    getProjectVisibilitySummary: build.query<
      ProjectVisibilitySummary,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/visibility-summary`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: ['VisibilitySummary']
    }),

    getProjectVisibilityTrend: build.query<
      VisibilityTrendResponse,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/visibility-trend`,
        method: 'POST',
        body: {
          ...buildTrackerRangeBody(options),
          ...(options.brandKeys?.length ? { brandKeys: options.brandKeys } : {})
        }
      }),
      providesTags: ['VisibilityTrend']
    }),

    getShareOfVoiceTrend: build.query<
      ShareOfVoiceTrendResponse,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/share-of-voice-trend`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: ['VisibilityTrend']
    }),

    getProjectExecutions: build.query<
      { rows: PromptHistoryItem[] },
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/insights/projects/${projectId}/executions`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: ['Executions']
    }),

    getExecutionInsight: build.query<ExecutionInsight, string>({
      query: executionId => `/insights/executions/${executionId}`,
      providesTags: (_r, _e, id) => [{ type: 'Execution', id }]
    }),

    getDomains: build.query<Domain[], string>({
      query: projectId => ({
        url: `/projects/${projectId}/domains/get`,
        method: 'POST'
      }),
      providesTags: ['Domains']
    }),

    createDomain: build.mutation<
      { id: string },
      {
        projectId: string;
        body: {
          host: string;
          isPrimary?: boolean;
          isTrackingSource?: boolean;
          displayName?: string;
        };
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/domains`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Domains', 'ProjectInsight']
    }),

    updateDomain: build.mutation<
      { id: string },
      { projectId: string; domainId: string; body: { host: string } }
    >({
      query: ({ projectId, domainId, body }) => ({
        url: `/projects/${projectId}/domains/${domainId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['Domains', 'ProjectInsight']
    }),

    getBrandAliases: build.query<ProjectBrandAlias[], string>({
      query: projectId => ({
        url: `/projects/${projectId}/brand-aliases/get`,
        method: 'POST'
      }),
      providesTags: ['BrandAliases']
    }),

    createBrandAlias: build.mutation<
      { id: string },
      {
        projectId: string;
        body: { name: string; language?: string; isPrimary?: boolean };
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/brand-aliases`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['BrandAliases', 'ProjectInsight']
    }),

    updateBrandAlias: build.mutation<
      { id: string },
      { projectId: string; aliasId: string; body: { name: string } }
    >({
      query: ({ projectId, aliasId, body }) => ({
        url: `/projects/${projectId}/brand-aliases/${aliasId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['BrandAliases', 'ProjectInsight']
    }),

    deleteBrandAlias: build.mutation<
      void,
      { projectId: string; aliasId: string }
    >({
      query: ({ projectId, aliasId }) => ({
        url: `/projects/${projectId}/brand-aliases/${aliasId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['BrandAliases', 'ProjectInsight']
    }),

    getCompetitors: build.query<CompetitorInsight[], string>({
      query: projectId => ({
        url: `/projects/${projectId}/competitors/get`,
        method: 'POST'
      }),
      providesTags: ['Competitors']
    }),

    getCompetitorIntelligence: build.query<
      CompetitorIntelligence,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/competitor-intelligence`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: ['CompetitorIntelligence']
    }),

    createCompetitor: build.mutation<
      { id: string },
      {
        projectId: string;
        body: { brandName: string; websiteUrl: string; aliases?: string[] };
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/competitors`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Competitors', 'CompetitorIntelligence']
    }),

    createCompetitorFromExtracted: build.mutation<
      { id: string },
      {
        projectId: string;
        body: { brandName: string; detectedHost?: string };
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/competitors/from-extracted`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Competitors', 'CompetitorIntelligence']
    }),

    updateCompetitor: build.mutation<
      { id: string },
      {
        projectId: string;
        competitorId: string;
        body: { brandName?: string; websiteUrl?: string; aliases?: string[] };
      }
    >({
      query: ({ projectId, competitorId, body }) => ({
        url: `/projects/${projectId}/competitors/${competitorId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['Competitors', 'CompetitorIntelligence']
    }),

    deleteCompetitor: build.mutation<
      void,
      { projectId: string; competitorId: string }
    >({
      query: ({ projectId, competitorId }) => ({
        url: `/projects/${projectId}/competitors/${competitorId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Competitors', 'CompetitorIntelligence']
    }),

    getSourceInsightsSummary: build.query<
      SourceInsightsSummary,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/source-insights/summary`,
        method: 'POST',
        body: buildSourceInsightsBody(options)
      }),
      providesTags: ['SourceInsights']
    }),

    getSourceInsightsDomains: build.query<
      SourceInsightsDomainsResult,
      { projectId: string } & TrackerDateRangeQuery & {
          page?: number;
          perPage?: number;
        }
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/source-insights/domains`,
        method: 'POST',
        body: buildSourceInsightsBody(options)
      }),
      providesTags: ['SourceInsights']
    }),

    getSourceInsightsTypes: build.query<
      SourceInsightsTypesResult,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/source-insights/types`,
        method: 'POST',
        body: buildSourceInsightsBody(options)
      }),
      providesTags: ['SourceInsights']
    }),

    getSourceInsightsTrend: build.query<
      SourceInsightsTrendResult,
      { projectId: string } & TrackerDateRangeQuery & { topN?: number }
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/source-insights/trend`,
        method: 'POST',
        body: {
          ...buildSourceInsightsBody(options),
          ...(options.topN != null ? { topN: options.topN } : {})
        }
      }),
      providesTags: ['SourceInsights']
    }),

    getSourceFilterOptions: build.query<
      SourceFilterOptionsResult,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/source-insights/filter-options`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: ['SourceInsights']
    }),

    getPromptSourceInsights: build.query<
      PromptSourceInsightsResult,
      { promptId: string } & TrackerDateRangeQuery
    >({
      query: ({ promptId, ...options }) => ({
        url: `/prompts/${promptId}/source-insights`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      })
    }),

    getBrandSourceInsights: build.query<
      BrandSourceInsightsResult,
      { projectId: string; brandKey: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, brandKey, ...options }) => ({
        url: `/projects/${projectId}/source-insights/brands/${encodeURIComponent(brandKey)}`,
        method: 'POST',
        body: buildSourceInsightsBody(options)
      })
    }),

    getCompetitorSourceGap: build.query<
      CompetitorSourceGapResult,
      { projectId: string } & TrackerDateRangeQuery
    >({
      query: ({ projectId, ...options }) => ({
        url: `/projects/${projectId}/source-insights/competitor-gap`,
        method: 'POST',
        body: buildSourceInsightsBody(options)
      }),
      providesTags: ['SourceInsights']
    }),

    getTopics: build.query<Topic[], string>({
      query: projectId => ({
        url: `/projects/${projectId}/topics/get`,
        method: 'POST'
      }),
      providesTags: ['Topics']
    }),

    createTopic: build.mutation<
      { id: string; name: string },
      {
        projectId: string;
        name: string;
        language?: string;
        description?: string;
        marketCountry?: string;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/topics`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Topics', 'ProjectInsight']
    }),

    updateTopic: build.mutation<
      { id: string },
      { projectId: string; topicId: string; body: { name: string } }
    >({
      query: ({ projectId, topicId, body }) => ({
        url: `/projects/${projectId}/topics/${topicId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['Topics', 'ProjectInsight']
    }),

    deleteTopics: build.mutation<
      void,
      { projectId: string; topicIds: string[]; deletePrompts?: boolean }
    >({
      query: ({ projectId, topicIds, deletePrompts = false }) => ({
        url: `/projects/${projectId}/topics`,
        method: 'DELETE',
        body: { ids: topicIds, deletePrompts }
      }),
      invalidatesTags: ['Topics', 'Prompts', 'ProjectInsight']
    }),

    suggestTopicPrompts: build.mutation<
      { suggestions: string[] },
      { topicId: string; modelId?: string }
    >({
      query: ({ topicId, modelId }) => ({
        url: `/topics/${topicId}/suggest-prompts`,
        method: 'POST',
        body: modelId ? { modelId } : {}
      })
    }),

    batchCreatePrompts: build.mutation<
      { ids: string[] } | null,
      { topicId: string; modelIds: string[]; prompts: string[] }
    >({
      query: ({ topicId, modelIds, prompts }) => ({
        url: `/topics/${topicId}/prompts/batch`,
        method: 'POST',
        body: {
          modelId: modelIds[0],
          modelIds,
          prompts: prompts.map(text => ({ text }))
        }
      }),
      invalidatesTags: ['Prompts', 'Topics', 'ProjectInsight']
    }),

    getPromptInsight: build.query<
      UserPromptInsight & {
        topicName: string;
        providerResponses: PromptProviderResponse[];
      },
      { promptId: string } & TrackerDateRangeQuery
    >({
      query: ({ promptId, ...options }) => ({
        url: `/insights/prompts/${promptId}`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: (_r, _e, arg) => [{ type: 'PromptInsight', id: arg.promptId }]
    }),

    getPromptExecutionHistory: build.query<
      PromptExecutionHistoryResponse,
      { promptId: string } & TrackerDateRangeQuery
    >({
      query: ({ promptId, ...options }) => ({
        url: `/insights/prompts/${promptId}/history`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: (_r, _e, arg) => [{ type: 'PromptHistory', id: arg.promptId }]
    }),

    getPromptBrandAggregation: build.query<
      PromptBrandAggregation,
      { promptId: string } & TrackerDateRangeQuery
    >({
      query: ({ promptId, ...options }) => ({
        url: `/prompts/${promptId}/brand-aggregation`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'PromptBrandAggregation', id: arg.promptId }
      ]
    }),

    getPromptOverview: build.query<
      PromptOverview,
      { promptId: string } & TrackerDateRangeQuery
    >({
      query: ({ promptId, ...options }) => ({
        url: `/prompts/${promptId}/overview`,
        method: 'POST',
        body: buildTrackerRangeBody(options)
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'PromptOverview', id: arg.promptId }
      ]
    }),

    createProjectPrompt: build.mutation<
      { id: string },
      {
        projectId: string;
        body: {
          text: string;
          modelId: string;
          modelIds?: string[];
          mode?: 'quick' | 'structured';
          topicId?: string;
          newTopicName?: string;
        };
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/prompts`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Prompts', 'Topics', 'ProjectInsight']
    }),

    createPrompt: build.mutation<
      { id: string },
      { topicId: string; text: string; modelId: string; modelIds?: string[] }
    >({
      query: ({ topicId, text, modelId, modelIds }) => ({
        url: `/topics/${topicId}/prompts`,
        method: 'POST',
        body: { text, modelId, modelIds }
      }),
      invalidatesTags: ['Prompts', 'Topics', 'ProjectInsight']
    }),

    getPromptRunBatch: build.query<PromptRunBatchStatus, string>({
      query: batchId => ({
        url: `/prompts/runs/${batchId}`,
        method: 'POST',
        body: { batchId }
      })
    }),

    enqueuePromptRuns: build.mutation<
      EnqueuePromptRunsResult,
      { promptIds?: string[]; topicId?: string; modelId?: string }
    >({
      query: body => ({
        url: '/prompts/run',
        method: 'POST',
        body
      }),
      invalidatesTags: [
        'Prompts',
        'Executions',
        'VisibilitySummary',
        'PromptInsight',
        'PromptHistory',
        'PromptOverview'
      ]
    }),

    /** @deprecated Sync run — prefer `enqueuePromptRuns`. Kept for tooling. */
    runPromptSync: build.mutation<
      RunPromptResult,
      { promptId: string; modelId?: string }
    >({
      query: ({ promptId, modelId }) => ({
        url: `/prompts/${promptId}/run`,
        method: 'POST',
        body: modelId ? { modelId } : {}
      }),
      invalidatesTags: [
        'Prompts',
        'Executions',
        'VisibilitySummary',
        'PromptInsight',
        'PromptHistory',
        'PromptOverview'
      ]
    }),

    updatePrompt: build.mutation<
      { id: string },
      {
        promptId: string;
        body: { text?: string; modelId?: string; modelIds?: string[] };
      }
    >({
      query: ({ promptId, body }) => ({
        url: `/prompts/${promptId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['Prompts', 'PromptInsight']
    }),

    deletePrompt: build.mutation<void, string[]>({
      query: ids => ({
        url: `/prompts`,
        method: 'DELETE',
        body: { ids }
      }),
      invalidatesTags: ['Prompts', 'ProjectInsight']
    }),

    reextractBrands: build.mutation<BrandReextractReport, string[]>({
      query: promptIds => ({
        url: '/prompts/reextract-brands',
        method: 'POST',
        body: { promptIds }
      }),
      invalidatesTags: [
        'Executions',
        'Execution',
        'PromptBrandAggregation',
        'PromptInsight',
        'PromptOverview',
        'ProjectInsight',
        'VisibilitySummary'
      ]
    }),

    getPublicModels: build.query<AiModel[], void>({
      query: () => '/models/public',
      providesTags: ['Models']
    })
  })
});

export const {
  useGetProjectsQuery,
  useCreateProjectMutation,
  useGetProjectQuery,
  useDeleteProjectMutation,
  useGetProjectActivityRangeQuery,
  useGetProjectInsightQuery,
  useGetProjectPromptsQuery,
  useGetProjectPromptListQuery,
  useFetchProjectPromptListPageMutation,
  useGetProjectTopicListQuery,
  useFetchProjectTopicListPageMutation,
  useGetProjectVisibilitySummaryQuery,
  useGetProjectVisibilityTrendQuery,
  useGetShareOfVoiceTrendQuery,
  useGetProjectExecutionsQuery,
  useGetExecutionInsightQuery,
  useGetDomainsQuery,
  useCreateDomainMutation,
  useUpdateDomainMutation,
  useGetBrandAliasesQuery,
  useCreateBrandAliasMutation,
  useUpdateBrandAliasMutation,
  useDeleteBrandAliasMutation,
  useGetCompetitorsQuery,
  useGetCompetitorIntelligenceQuery,
  useCreateCompetitorMutation,
  useCreateCompetitorFromExtractedMutation,
  useUpdateCompetitorMutation,
  useDeleteCompetitorMutation,
  useGetSourceInsightsSummaryQuery,
  useGetSourceInsightsDomainsQuery,
  useGetSourceInsightsTypesQuery,
  useGetSourceInsightsTrendQuery,
  useGetSourceFilterOptionsQuery,
  useGetPromptSourceInsightsQuery,
  useGetBrandSourceInsightsQuery,
  useGetCompetitorSourceGapQuery,
  useGetTopicsQuery,
  useCreateTopicMutation,
  useUpdateTopicMutation,
  useDeleteTopicsMutation,
  useSuggestTopicPromptsMutation,
  useBatchCreatePromptsMutation,
  useGetPromptInsightQuery,
  useGetPromptExecutionHistoryQuery,
  useGetPromptBrandAggregationQuery,
  useGetPromptOverviewQuery,
  useCreateProjectPromptMutation,
  useCreatePromptMutation,
  useEnqueuePromptRunsMutation,
  useRunPromptSyncMutation,
  useUpdatePromptMutation,
  useDeletePromptMutation,
  useReextractBrandsMutation,
  useGetPublicModelsQuery,
  useLazyGetProjectsQuery,
  useLazyGetProjectPromptsQuery,
  useLazyGetProjectVisibilitySummaryQuery,
  useLazyGetProjectExecutionsQuery,
  useLazyGetExecutionInsightQuery,
  useLazyGetPromptInsightQuery,
  useLazyGetCompetitorSourceGapQuery,
  useLazyGetBrandSourceInsightsQuery
} = trackerEndpointsApi;

export default trackerEndpointsApi;
